export const createDecks = ({ helpers, asset }) => {
  const {
    ExternalLink,
    SoftCard,
    EmojiCard,
    CleanList,
    SectionLabel,
    ConceptMark,
    ScreenshotFrame,
    NumberedList,
    ComparisonTable,
    BigStatement,
    Timeline,
  } = helpers;

  return {
    overview: {
      label: "Overview",
      description: "core pitch",
      slides: [
        {
          title: "",
          density: "medium",
          content: (
            <div className="relative overflow-hidden px-3 py-2 text-center sm:px-4">
              <div className="absolute right-3 top-3 sm:right-4 sm:top-4">
                <ExternalLink compact />
              </div>

              <div className="mx-auto max-w-7xl">
                <div className="mb-4 inline-flex rounded-full border border-red-200 bg-red-50 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-red-700 sm:text-xs">
                  the post-course plateau
                </div>

                <h1 className="font-mono text-4xl font-black leading-[0.92] tracking-tight text-gray-950 sm:text-5xl md:text-6xl xl:text-7xl">
                  You finished
                  <br />
                  the language course.
                  <br />
                  Now what?
                </h1>

                <p className="mx-auto mt-5 max-w-4xl text-base leading-relaxed text-gray-700 sm:text-xl xl:text-2xl">
                  Many learners can communicate, but still do not feel comfortable speaking.
                </p>

                <div className="mx-auto my-5 h-1.5 w-48 rounded-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-500 sm:w-80" />

                <div className="grid gap-4 text-left md:grid-cols-3 xl:gap-5">
                  <SoftCard className="border-red-100 bg-white/75 shadow-none">
                    <div className="mb-3 text-3xl">😶</div>
                    <div className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                      Speech still feels fragile
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      pronunciation, rhythm, timing, confidence
                    </p>
                  </SoftCard>

                  <SoftCard className="border-amber-100 bg-white/75 shadow-none">
                    <div className="mb-3 text-3xl">🏫</div>
                    <div className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                      Traditional courses solve part of the problem
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      learners complete the course, yet spoken confidence and natural interaction still lag behind
                    </p>
                  </SoftCard>

                  <SoftCard className="border-purple-100 bg-white/75 shadow-none">
                    <div className="mb-3 text-3xl">🕳️</div>
                    <div className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                      There is no clear next step
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600 xl:text-base">
                      after the course, speaking practice becomes irregular, unstructured, and hard to sustain
                    </p>
                  </SoftCard>
                </div>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Existing Options Help — But Each Leaves A Gap",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Learners try several paths outside the classroom.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Existing approaches are useful — but spoken progress usually needs structure,
                feedback, repetition, and real social interaction together.
              </p>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 xl:gap-5">
                {[
                  [
                    "🌍",
                    "Immersion",
                    "great for exposure",
                    "but without feedback and structure, learners may stay in fixed speaking routines",
                  ],
                  [
                    "📱",
                    "Language apps",
                    "great for vocabulary refresh",
                    "but usually limited in pronunciation, dialogue, and real social interaction",
                  ],
                  [
                    "🧑‍🏫",
                    "Private tutors",
                    "great for correction",
                    "but one-to-one practice can feel evaluative, tense, and socially narrow",
                  ],
                  [
                    "☕",
                    "Language cafés",
                    "great for community",
                    "but often lack continuity, preparation, and progression",
                  ],
                ].map(([emoji, title, strength, gap]) => (
                  <SoftCard
                    key={title}
                    className="bg-white/80 text-left shadow-none ring-1 ring-blue-100"
                  >
                    <div className="mb-3 text-3xl">{emoji}</div>
                    <h4 className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                      {title}
                    </h4>
                    <div className="mb-3 text-xs font-black uppercase tracking-[0.14em] text-blue-600 xl:text-sm">
                      {strength}
                    </div>
                    <p className="text-sm leading-relaxed text-gray-600">{gap}</p>
                  </SoftCard>
                ))}
              </div>

              <BigStatement title="The missing piece is structured social speech practice." tone="purple" compact>
                Learners need to speak, receive feedback, hear others improve,
                and repeat practice in a less judgmental social environment.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
        },

        {
          title: "Peer Review Layer",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4">
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Rate Me turns pronunciation training into a shared listening task:
                ask for feedback, review others, and improve by explaining what you hear.
              </p>

              <div className="grid items-stretch gap-4 lg:grid-cols-[1fr_auto_1fr] xl:gap-5">
                <SoftCard className="bg-gradient-to-br from-blue-50/80 to-white ring-1 ring-blue-100">
                  <div className="mb-4 text-center">
                    <div className="mb-2 text-3xl">🎧</div>
                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      Individual preparation
                    </h4>
                  </div>

                  <CleanList
                    accent="blue"
                    items={[
                      "echoing and shadowing",
                      "AI-supported feedback",
                      "prosody curves",
                      "articulation guidance",
                      "specialized pronunciation courses",
                      "L1-sensitive sound help",
                    ]}
                  />
                </SoftCard>

                <div className="hidden items-center justify-center lg:flex">
                  <div className="rounded-full bg-white/80 px-5 py-4 text-4xl font-black text-purple-300 shadow-sm ring-1 ring-purple-100">
                    →
                  </div>
                </div>

                <SoftCard className="bg-gradient-to-br from-purple-50/90 to-white ring-1 ring-purple-100">
                  <div className="mb-4 text-center">
                    <div className="mb-2 text-3xl">💬</div>
                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      Peer review
                    </h4>
                  </div>

                  <CleanList
                    accent="purple"
                    items={[
                      "ask others to review speech",
                      "compare with model audio",
                      "mark unclear pronunciation",
                      "reviewing trains listening",
                      "mutual repair through comments",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement title="Feedback helps both sides." tone="green" compact>
                The learner receives correction. The reviewer trains perception,
                attention, and pronunciation awareness.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
        },

        {
          title: "Community Feedback Improves The System",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 text-center sm:px-4">
              <p className="mx-auto max-w-4xl text-sm leading-relaxed text-gray-700 sm:text-base xl:text-lg">
                Teachers and advanced users can leave comments and suggestions.
                The best feedback can become reusable L1→L2 guidance.
              </p>

              <div className="grid items-center gap-5 xl:grid-cols-[1.55fr_0.45fr] xl:gap-6">
                <div className="group relative overflow-visible">
                  <a href={asset("rated.png")} target="_blank" rel="noopener noreferrer" className="block">
                    <div className="overflow-hidden rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.11)] transition-all duration-300 group-hover:scale-[1.01] group-hover:shadow-[0_35px_100px_rgba(15,23,42,0.18)]">
                      <div className="h-[300px] overflow-hidden rounded-[1.3rem] border border-gray-100 bg-white sm:h-[360px] xl:h-[390px]">
                        <img
                          src={asset("rated.png")}
                          alt="Community pronunciation review with marked words and comment"
                          className="h-full w-full object-cover object-[center_68%]"
                        />
                      </div>

                      <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                        Pronunciation request with ratings, marked words, comments, and reusable feedback
                      </div>
                    </div>

                    <div className="pointer-events-none absolute right-5 top-5 hidden opacity-0 transition-opacity duration-300 group-hover:opacity-100 lg:block">
                      <div className="rounded-full bg-white/95 px-4 py-2 text-sm font-black text-gray-700 shadow-lg ring-1 ring-gray-200 backdrop-blur-sm">
                        🔍 Click to enlarge
                      </div>
                    </div>
                  </a>
                </div>

                <SoftCard className="bg-gradient-to-br from-emerald-50/95 to-white text-left shadow-none ring-1 ring-emerald-100">
                  <div className="mb-4 text-center">
                    <div className="mb-2 text-3xl">🧠</div>
                    <h4 className="text-xl font-black text-gray-950">
                      Shared intelligence
                    </h4>
                  </div>

                  <CleanList
                    accent="green"
                    items={[
                      "advanced users leave suggestions",
                      "best comments surface first",
                      "feedback becomes reusable guidance",
                      "detect L1→L2 pronunciation patterns",
                      "fine-tune AI assessment",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement
                title="The platform gradually accumulates L1-sensitive speech knowledge."
                tone="green"
                compact
              >
                Community corrections can support recommendations adapted to learners
                with similar language backgrounds.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "From Exercises To Shared Dialogue",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Catchphraser can extend pronunciation training into collaborative dialogue practice.
              </p>

              <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
                <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-center ring-1 ring-blue-100">
                  <div className="mb-3 text-4xl">🎧</div>
                  <h4 className="mb-3 text-xl font-black text-gray-950 xl:text-2xl">Prepare</h4>
                  <p className="text-sm leading-relaxed text-gray-700 xl:text-base">
                    learners practice lines individually with model audio,
                    prosody tools, and pronunciation feedback
                  </p>
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-center ring-1 ring-purple-100">
                  <div className="mb-3 text-4xl">💬</div>
                  <h4 className="mb-3 text-xl font-black text-gray-950 xl:text-2xl">Review</h4>
                  <p className="text-sm leading-relaxed text-gray-700 xl:text-base">
                    participants review each other before the session
                    and discuss unclear pronunciation together
                  </p>
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-pink-50 to-white text-center ring-1 ring-pink-100">
                  <div className="mb-3 text-4xl">👥</div>
                  <h4 className="mb-3 text-xl font-black text-gray-950 xl:text-2xl">Rehearse</h4>
                  <p className="text-sm leading-relaxed text-gray-700 xl:text-base">
                    groups read dialogues together,
                    switch roles, repeat scenes,
                    and gradually improve coordination
                  </p>
                </SoftCard>
              </div>

              <BigStatement
                title="The script removes the pressure of inventing what to say."
                tone="purple"
                compact
              >
                Learners can focus on pronunciation, timing, rhythm,
                emotion, listening, and response.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),radial-gradient(circle_at_bottom_right,_#fce7f3,_transparent_32%),linear-gradient(135deg,#ffffff,#faf5ff)]",
        },

        {
          title: "Rehearsal Creates Purpose",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <p className="mx-auto max-w-5xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Rehearsal changes repetition from an endless streak loop
                into preparation for a shared social goal.
              </p>

              <div className="grid gap-5 lg:grid-cols-2 xl:gap-6">
                <SoftCard className="bg-gradient-to-br from-amber-50 to-white text-left ring-1 ring-amber-100">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="text-4xl">🔁</div>
                    <div>
                      <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                        Endless repetition
                      </h4>
                      <div className="text-xs font-bold uppercase tracking-[0.14em] text-amber-700 xl:text-sm">
                        common language app pattern
                      </div>
                    </div>
                  </div>

                  <CleanList
                    accent="amber"
                    items={[
                      "isolated daily streaks",
                      "practice without social context",
                      "repetition without shared purpose",
                      "motivation fades over time",
                      "speech remains individual",
                    ]}
                  />
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left ring-1 ring-purple-100">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="text-4xl">🎭</div>
                    <div>
                      <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                        Goal-oriented rehearsal
                      </h4>
                      <div className="text-xs font-bold uppercase tracking-[0.14em] text-purple-700 xl:text-sm">
                        collaborative preparation
                      </div>
                    </div>
                  </div>

                  <CleanList
                    accent="purple"
                    items={[
                      "prepare for a shared session",
                      "practice with other participants",
                      "improve toward a visible event",
                      "dialogues create social involvement",
                      "speech becomes coordinated activity",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement
                title="People naturally improve when preparing for something together."
                tone="purple"
                compact
              >
                Meetings, concerts, theater rehearsals, and presentations all create
                motivation through shared deadlines, coordination, and social commitment.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#fef3c7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
        },

        {
          title: "Later Step: Guided Speech Events",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <p className="mx-auto max-w-5xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                The bolder move is to let teachers, tutors, and theater-related professionals
                organize guided practice — closer to a class, but focused on speech production.
              </p>

              <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr] xl:gap-6">
                <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left ring-1 ring-purple-100">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="text-4xl">🧑‍🏫</div>
                    <div>
                      <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                        Guided format
                      </h4>
                      <div className="text-xs font-bold uppercase tracking-[0.14em] text-purple-700 xl:text-sm">
                        paid or institutional layer
                      </div>
                    </div>
                  </div>

                  <CleanList
                    accent="purple"
                    items={[
                      "teacher-led dialogue sessions",
                      "guided correction in real time",
                      "extra model voice and pragmatic examples",
                      "acting and voice tricks for delivery",
                      "structured progression across meetings",
                    ]}
                  />
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-emerald-50 to-white text-left ring-1 ring-emerald-100">
                  <div className="mb-4 flex items-center gap-3">
                    <div className="text-4xl">🎙️</div>
                    <div>
                      <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                        Event formats
                      </h4>
                      <div className="text-xs font-bold uppercase tracking-[0.14em] text-emerald-700 xl:text-sm">
                        social practice with a subject
                      </div>
                    </div>
                  </div>

                  <CleanList
                    accent="green"
                    items={[
                      "small online rehearsal groups",
                      "radio-play reading sessions",
                      "larger dialogues or staged readings",
                      "offline language café events",
                      "speech clubs around specific themes",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement
                title="This makes teachers and facilitators part of the platform economy."
                tone="green"
                compact
              >
                Like gym trainers, guides can organize recurring practice, raise quality,
                create social commitment, and make participation feel purposeful.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_32%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

       {
  title: "The Ecosystem Around Speech Practice",
  density: "dense",
  content: (
    <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4">
      <div>
        <div className="mb-3 inline-flex rounded-full bg-white/85 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-emerald-700 shadow-sm ring-1 ring-emerald-200 sm:text-xs">
          community • guided practice • language infrastructure
        </div>

        <p className="mx-auto max-w-5xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
          Catchphraser can evolve from a pronunciation platform into infrastructure
          for coordinated language practice: peer review, rehearsal loops,
          guided events, community-created material, and accumulated L1→L2 expertise.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-[0.95fr_1.05fr]">
        <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left ring-1 ring-blue-100">
          <div className="mb-4 flex items-center gap-3">
            <div className="text-4xl">⚙️</div>

            <div>
              <h4 className="text-2xl font-black text-gray-950">
                Catchphraser provides
              </h4>

              <div className="text-sm font-bold uppercase tracking-[0.14em] text-blue-700">
                core infrastructure
              </div>
            </div>
          </div>

          <CleanList
            accent="blue"
            items={[
              "speech analysis and pronunciation tools",
              "prosody and rhythm visualization",
              "peer-review workflows",
              "L1→L2 language expertise",
              "metrics, progression, and coordination tools",
            ]}
          />
        </SoftCard>

        <SoftCard className="bg-gradient-to-br from-emerald-50 to-white text-left ring-1 ring-emerald-100">
          <div className="mb-4 flex items-center gap-3">
            <div className="text-4xl">🌱</div>

            <div>
              <h4 className="text-2xl font-black text-gray-950">
                The ecosystem contributes
              </h4>

              <div className="text-sm font-bold uppercase tracking-[0.14em] text-emerald-700">
                social and pedagogical growth
              </div>
            </div>
          </div>

          <CleanList
            accent="green"
            items={[
              "community moderation and feedback",
              "guided practice formats",
              "new rehearsal cultures",
              "shared pronunciation knowledge",
              "new content and event formats",
            ]}
          />
        </SoftCard>
      </div>

      <BigStatement
        title="A competitor can copy features faster than they can copy accumulated practice culture."
        tone="green"
        compact
      >
        The durable value comes from the interaction between technology,
        speech science, guided practice, reusable feedback, institutional participation,
        and a growing community of people practicing together.
      </BigStatement>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
},
      ],
    },

 pedagogy: {
  label: "Pedagogy",
  description: "learning theory",
  slides: [
    {
      title: "Speech Is A Coordination Skill",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “Learning awakens a variety of internal developmental processes
              that operate only when interacting with others.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Pronunciation is not only knowledge.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Speech involves perception, articulation, rhythm, timing,
            and social responsiveness. It is closer to a coordination skill
            than to a rule that can simply be explained.
          </p>

          <div className="grid gap-4 md:grid-cols-4 xl:gap-5">
            {[
              ["👂", "Ear", "perception"],
              ["🗣️", "Voice", "articulation"],
              ["🎵", "Rhythm", "timing"],
              ["👥", "Interaction", "response"],
            ].map(([emoji, title, text]) => (
              <EmojiCard
                key={title}
                emoji={emoji}
                title={title}
                text={text}
                className="ring-1 ring-blue-100"
              />
            ))}
          </div>

          <BigStatement title="Like music, sports, or theater" compact>
            speech requires repeated embodied practice, not only explanation.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Scripts Reduce Cognitive Load",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Pedagogical premise
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Learners can focus more deeply on delivery when the language material
              is already structured.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            When the words are given, learners can focus on speech.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Scripts reduce pressure to invent content. That makes room for
            pronunciation, rhythm, intonation, timing, emotion, and response.
          </p>

          <ComparisonTable
            leftTitle="Free conversation"
            rightTitle="Script-based dialogue"
            rows={[
              ["what should I say?", "how should I say it?"],
              ["grammar pressure", "delivery focus"],
              ["topic search", "voice and timing"],
              ["conversation anxiety", "prepared interaction"],
            ]}
          />

          <BigStatement title="The script is not the goal." tone="purple" compact>
            It is a scaffold for socially usable speech.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Structured Progression",
      density: "screenshot",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 sm:px-4">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 text-center shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Pedagogical direction
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Good pronunciation work needs progression: model, imitation,
              noticing, feedback, repetition, and transfer into use.
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
              Pronunciation training with pedagogical structure.
            </h3>
          </div>

          <div className="grid items-center gap-5 lg:grid-cols-[0.9fr_1.1fr] xl:gap-7">
            <SoftCard className="bg-gradient-to-br from-purple-50 to-violet-50 text-center ring-1 ring-purple-100">
              <div className="mx-auto mb-4 h-24 w-24 overflow-hidden rounded-full border-4 border-white shadow-xl xl:h-28 xl:w-28">
                <img
                  src={asset("oh.jpg")}
                  alt="Associate Professor Olaf Husby"
                  className="h-full w-full object-cover"
                />
              </div>

              <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                Assoc. Prof. Olaf Husby
              </h4>

              <p className="mt-1 font-bold text-purple-700">
                Course Author – Teaching & Didactics
              </p>

              <p className="mt-3 text-sm leading-relaxed text-gray-700">
                Phonetician, second-language educator, course developer,
                and Norwegian dialect expert.
              </p>
            </SoftCard>

            <div className="space-y-4">
              <SoftCard>
                <h4 className="mb-3 text-xl font-black text-gray-950">
                  Flyt og rytme systematically trains
                </h4>

                <CleanList
                  items={[
                    "sentence rhythm and stress alternation",
                    "prosodic foot structure and anacrusis control",
                    "chaining across word boundaries",
                    "reduction and connected speech processes",
                    "echoing → shadowing progression toward automaticity",
                  ]}
                />
              </SoftCard>

              <BigStatement title="Structured from foundation to mastery" compact>
                Courses can function as preparation for later rehearsal,
                reading groups, and collaborative performance.
              </BigStatement>
            </div>
          </div>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Feedback Develops Perception",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Pedagogical premise
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Learners do not only need correction; they need to learn what to notice.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Reviewing others can improve the reviewer.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            When learners compare attempts with model audio, they train attention:
            unclear sounds, stress, rhythm, intonation, and repair strategies.
          </p>

          <div className="grid items-stretch gap-4 text-center sm:grid-cols-2 md:grid-cols-5">
            {[
              ["🎧", "Listen"],
              ["🔍", "Notice"],
              ["💬", "Explain"],
              ["🔁", "Repair"],
              ["🎯", "Transfer"],
            ].map(([emoji, title]) => (
              <EmojiCard key={title} emoji={emoji} title={title} />
            ))}
          </div>

          <BigStatement title="Feedback is not only correction." tone="green" compact>
            It is a way to develop a better ear.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },
  ],
},

    technology: {
      label: "Technology",
      description: "product workflows",
      slides: [
        {
          title: "AI-Supported Pronunciation Training",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl px-3 py-0 sm:px-4">
              <div className="grid items-center gap-5 xl:grid-cols-[1.42fr_0.58fr] xl:gap-7">
                <div className="space-y-4 text-center xl:space-y-5">
                  <h3 className="mx-auto max-w-4xl text-3xl font-black leading-tight text-gray-950 xl:text-4xl">
                    AI feedback gives learners a private preparation loop.
                  </h3>

                  <div className="mx-auto max-w-4xl rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.11)]">
                    <img
                      src={asset("kattenspiserikkefisk.png")}
                      alt="AI pronunciation feedback interface"
                      className="w-full rounded-[1.2rem] border border-gray-100 object-contain"
                    />

                    <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                      AI-supported pronunciation practice with phonetic feedback
                    </div>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-1">
                  <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left shadow-none ring-1 ring-blue-100">
                    <h4 className="mb-3 text-xl font-black text-gray-950">
                      Private preparation
                    </h4>

                    <CleanList
                      accent="blue"
                      items={[
                        "safe repetition",
                        "immediate feedback",
                        "pronunciation correction",
                        "unlimited attempts",
                      ]}
                    />
                  </SoftCard>

                  <SoftCard className="bg-gradient-to-br from-purple-50 to-white text-left shadow-none ring-1 ring-purple-100">
                    <h4 className="mb-3 text-xl font-black text-gray-950">
                      Human activation
                    </h4>

                    <CleanList
                      accent="purple"
                      items={[
                        "real listener response",
                        "meaningful explanation",
                        "social interpretation",
                        "collaborative attention",
                      ]}
                    />
                  </SoftCard>
                </div>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#eff6ff)]",
        },

        {
          title: "Community Pronunciation Review",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 sm:px-4 xl:space-y-5">
              <div className="text-center">
                <SectionLabel>Product Workflow</SectionLabel>
                <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                  Learners can ask the community to review pronunciation.
                </h3>
              </div>

              <div className="grid items-center gap-5 lg:grid-cols-[1.35fr_0.65fr] xl:gap-7">
                <ScreenshotFrame
                  src={asset("rateme.png")}
                  alt="Rate Me pronunciation review interface"
                  caption="Learner request for structured pronunciation feedback"
                  imgClassName="max-h-[430px] object-contain bg-gray-50 xl:max-h-[500px]"
                />

                <SoftCard className="bg-blue-50/70 ring-1 ring-blue-100">
                  <h4 className="mb-4 text-xl font-black text-gray-950 xl:text-2xl">
                    Reviewers can
                  </h4>
                  <CleanList
                    accent="blue"
                    items={[
                      "replay the learner attempt",
                      "compare it with narrator audio",
                      "inspect rhythm and melody",
                      "mark unclear words or sounds",
                      "leave concrete suggestions",
                    ]}
                  />
                </SoftCard>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_35%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Collaborative Repair",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-6xl space-y-4 px-3 py-0 text-center sm:px-4">
              <div className="mx-auto max-w-4xl rounded-[1.75rem] border border-gray-200/80 bg-white p-3 shadow-[0_22px_70px_rgba(15,23,42,0.10)]">
                <img
                  src={asset("rated.png")}
                  alt="Collaborative pronunciation review"
                  className="max-h-[300px] w-full rounded-[1.2rem] border border-gray-100 object-contain sm:max-h-[340px] xl:max-h-[365px]"
                />

                <div className="px-2 pt-2 text-center text-xs font-medium text-gray-500">
                  Pronunciation requests with ratings, marked segments, and discussion
                </div>
              </div>

              <div className="mx-auto max-w-4xl rounded-3xl border border-white/70 bg-gradient-to-r from-blue-50 via-white to-purple-50 px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)] sm:px-6">
                <p className="text-base font-semibold leading-relaxed text-gray-700 sm:text-lg">
                  Learners improve not only by speaking, but by listening,
                  evaluating, explaining, and repairing together.
                </p>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#f0fdf4,#faf5ff)]",
        },

        {
          title: "L1-Sensitive Feedback",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                The same mistake often has different causes.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Feedback becomes more useful when it knows what language background
                the learner is coming from.
              </p>

              <ComparisonTable
                leftTitle="Observed patterns"
                rightTitle="Adaptive guidance"
                leftColor="green"
                rows={[
                  ["learners mark difficult words", "explanations fit the learner’s L1"],
                  ["reviewers notice recurring errors", "practice targets the likely cause"],
                  ["similar learners struggle in similar places", "examples become more precise"],
                  ["corrections accumulate over time", "feedback becomes reusable"],
                ]}
              />

              <BigStatement
                title="This creates accumulated pronunciation knowledge."
                tone="green"
                compact
              >
                A Russian speaker and a French speaker learning Norwegian may need
                different explanations, examples, and repair paths.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
        },

        {
          title: "Prosody Visualization",
          density: "screenshot",
          content: (
            <div className="mx-auto max-w-7xl space-y-4 px-3 py-0 text-center sm:px-4">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Seeing rhythm, stress, and intonation.
              </h3>

              <ScreenshotFrame
                src={asset("muchwarmerthere.png")}
                alt="Pitch contour visualization showing intonation groups and pitch variation across phrase boundaries"
                className="mx-auto max-w-6xl"
                imgClassName="max-h-[270px] w-full object-contain bg-gray-50 sm:max-h-[310px] xl:max-h-[335px]"
              />

              <div className="grid gap-4 md:grid-cols-3">
                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-blue-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Intonation groups
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    Pitch develops across whole phrases, not only isolated words.
                  </p>
                </SoftCard>

                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-purple-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-600">
                    Word boundaries
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    The contour travels through connected speech and reveals phrasing.
                  </p>
                </SoftCard>

                <SoftCard className="bg-white/80 text-left shadow-none ring-1 ring-pink-100">
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-600">
                    Stress perception
                  </div>

                  <p className="text-sm leading-relaxed text-gray-700">
                    Timing, prominence, and emphasis become easier to notice.
                  </p>
                </SoftCard>
              </div>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
        },
      ],
    },

 community: {
  label: "Community",
  description: "social speech practice",
  slides: [
    {
      title: "Speaking Develops Socially",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-200 bg-gradient-to-r from-purple-100 via-white to-pink-100 px-5 py-4 shadow-[0_18px_50px_rgba(15,23,42,0.08)]">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-lg italic leading-relaxed text-gray-800 sm:text-xl">
              “Human learning presupposes a specific social nature.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Speaking is not only language production.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            People learn to speak through interaction:
            listening, responding, repairing misunderstandings,
            adjusting timing, and gradually becoming comfortable
            participating with others.
          </p>

          <ComparisonTable
            leftTitle="Private AI practice"
            rightTitle="Social speech rehearsal"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["individual repetition", "interaction with listeners"],
              ["private correction", "shared repair and feedback"],
              ["simulated conversation", "social coordination"],
              ["isolated learning loop", "visible group progression"],
            ]}
          />

          <BigStatement
            title="AI can simulate conversation, but speech is still fundamentally social."
            tone="purple"
            compact
          >
            Catchphraser uses scripts, peer review, rehearsal,
            and guided interaction to help learners move from
            isolated practice toward real participation with other people.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Peers Make Progress Visible",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “What a learner can do in cooperation today,
              they can do independently tomorrow.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners improve by hearing others improve.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Native-speaker models show the target,
            but peers show the process:
            hesitation, repair, adaptation, and gradual fluency.
          </p>

          <ComparisonTable
            leftTitle="Models"
            rightTitle="Peers"
            rows={[
              ["show the target", "show the path"],
              ["ideal pronunciation", "visible gradual improvement"],
              ["reference", "participation"],
              ["polished delivery", "realistic variability"],
            ]}
          />

          <BigStatement title="Speech confidence grows socially." tone="purple" compact>
            Learners become less inhibited when they hear people like themselves
            struggling, repairing, and gradually improving together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Shared Practice Reduces Pressure",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Mikhail Bakhtin
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “To be means to communicate.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners speak more when they feel safe enough to continue.
          </h3>

          <ComparisonTable
            leftTitle="High-pressure speech"
            rightTitle="Shared rehearsal"
            leftColor="amber"
            rightColor="green"
            rows={[
              ["one learner is evaluated", "everyone is visibly learning"],
              ["mistakes feel personal", "mistakes become normal"],
              ["perfect models create pressure", "similar learners reduce shame"],
              ["correction interrupts confidence", "repair becomes collaborative"],
            ]}
          />

          <BigStatement title="The goal is usable participation." tone="green" compact>
            Learners gradually become more comfortable speaking because
            speech becomes shared work rather than isolated performance.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#ecfdf5)]",
    },

    {
      title: "Dialogue Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

        <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
  <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
    Mikhail Bakhtin
  </div>

  <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
    “The utterance is filled with dialogic overtones.”
  </div>
</div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Prepared dialogue turns speaking into coordination.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Instead of inventing every sentence from scratch,
            learners can focus on timing, pronunciation,
            response, listening, and interaction.
          </p>

          <Timeline
            steps={[
              ["🎧", "Prepare", "individual rehearsal", "listen, repeat, inspect prosody", "ring-blue-100"],
              ["💬", "Review", "peer correction", "mark unclear rhythm and sounds", "ring-purple-100"],
              ["👥", "Perform", "dialogue together", "response timing and coordination", "ring-pink-100"],
            ]}
          />

          <BigStatement title="The script is a scaffold for social speech." tone="purple" compact>
            Learners move gradually from guided rehearsal toward more natural interaction.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Guided Social Events",
      density: "dense",
      content: (
        <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-pink-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-pink-700">
              Lev Vygotsky
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              “Through others we become ourselves.”
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Guided events create stronger social motivation.
          </h3>

          <div className="grid gap-5 md:grid-cols-3 xl:gap-6">
            <EmojiCard
              emoji="👥"
              title="Learners"
              text="prepare, review, rehearse"
              className="ring-1 ring-blue-100"
            />

            <EmojiCard
              emoji="🧑‍🏫"
              title="Guides"
              text="structure sessions and progression"
              className="ring-1 ring-purple-100"
            />

            <EmojiCard
              emoji="🎙️"
              title="Events"
              text="radio plays, workshops, staged readings"
              className="ring-1 ring-pink-100"
            />
          </div>

          <BigStatement title="Speech practice becomes a coordinated social activity." tone="purple" compact>
            The long-term direction is not only AI tutoring,
            but communities practicing speech together.
          </BigStatement>
        </div>
      ),
      bgColor: "bg-[linear-gradient(135deg,#ffffff,#faf5ff)]",
    },
  ],
},


gamification: {
  label: "Gamification",gamification: {
  label: "Interactive Practice",
  description: "rehearsal & dialogue challenges",
  slides: [
   {
  title: "Speech As Rehearsal",
  density: "dense",
  content: (
    <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

      <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
          Central analogy
        </div>

        <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
          An actor learns a role by rehearsing lines together with others.
          Speech practice can work in a similar way.
        </div>
      </div>

      <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
        Learners rehearse dialogue roles.
      </h3>

      <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
        The system plays the opposite role while the learner practices responses,
        timing, pronunciation, and delivery inside a structured interaction.
      </p>

      <div className="grid gap-4 md:grid-cols-3 xl:gap-5">

        <SoftCard className="text-left ring-1 ring-blue-100">
          <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
            Round 1
          </div>

          <h4 className="mb-3 text-xl font-black text-gray-950">
            Learn the lines
          </h4>

          <CleanList
            accent="blue"
            items={[
              "hear all dialogue lines",
              "repeat own and opponent lines",
              "practice pronunciation and rhythm",
              "become familiar with the scene",
            ]}
          />
        </SoftCard>

        <SoftCard className="text-left ring-1 ring-purple-100">
          <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-700">
            Round 2
          </div>

          <h4 className="mb-3 text-xl font-black text-gray-950">
            Rehearse the scene
          </h4>

          <CleanList
            accent="purple"
            items={[
              "only opponent lines are played",
              "produce missing responses",
              "practice timing and recall",
              "stay inside interaction flow",
            ]}
          />
        </SoftCard>

        <SoftCard className="text-left ring-1 ring-pink-100">
          <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-700">
            Round 3
          </div>

          <h4 className="mb-3 text-xl font-black text-gray-950">
            Perform the role
          </h4>

          <CleanList
            accent="pink"
            items={[
              "minimal or no support",
              "score depends on hint usage",
              "AI evaluates performance",
              "pronunciation and timing assessment",
            ]}
          />
        </SoftCard>

      </div>

      <BigStatement
        title="The learner does not only memorize sentences."
        tone="purple"
        compact
      >
        The learner rehearses timing, response, pronunciation,
        and participation inside structured interaction.
      </BigStatement>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
},

    {
      title: "Scaffolding And Progression",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Guided difficulty
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Support gradually disappears as the learner becomes more confident.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Hints function as temporary scaffolding.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system can provide different levels of support while keeping
            the learner inside the interaction loop.
          </p>

          <ComparisonTable
            leftTitle="Hint type"
            rightTitle="Learning function"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["first-word cue", "small retrieval support"],
              ["shuffled words", "sentence reconstruction"],
              ["audio replay", "prosody and pronunciation support"],
              ["audio + text", "maximum scaffold"],
            ]}
          />

          <BigStatement
            title="More support gives lower scores."
            tone="green"
            compact
          >
            The scoring system rewards increasing independence,
            while still allowing learners to continue practicing safely.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

   {
  title: "Why The Game Loop Is Pedagogically Sound",
  density: "dense",
  content: (
    <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

      <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
          Pedagogical logic
        </div>

        <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
          The game mechanics are derived from rehearsal, retrieval,
          scaffolding, and repeated exposure to useful language patterns.
        </div>
      </div>

      <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
        The interaction loop supports speech automaticity.
      </h3>

      <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
        The learner responds to a spoken cue, not just a written prompt.
        This trains pronunciation, intonation, rhythm, and timing inside
        a realistic dialogue exchange.
      </p>

      <ComparisonTable
        leftTitle="Learning principle"
        rightTitle="Interactive implementation"
        leftColor="green"
        rightColor="purple"
        rows={[
          ["repeated language exposure", "hear dialogue patterns many times"],
          ["chunk and pattern learning", "practice complete useful responses"],
          ["retrieval practice", "produce missing lines from memory"],
          ["prosodic response", "answer spoken cues with rhythm and intonation"],
          ["gradual scaffolding removal", "reduce hints over time"],
        ]}
      />

      <BigStatement
        title="The score measures independence, delivery, and timing."
        tone="green"
        compact
      >
        Fewer hints, fewer retries, clearer pronunciation, and more natural intonation
        show movement from guided rehearsal toward increasingly automatic spoken participation.
      </BigStatement>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
},
{
  title: "Challenge Others",
  density: "dense",
  content: (
    <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

      <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
          Social rehearsal
        </div>

        <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
          When the learner is ready, the same scene can become a shared challenge.
        </div>
      </div>

      <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
        Learners can challenge others to perform the same role.
      </h3>

      <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
        The same dialogue task can be sent to another learner.
        Both performances are scored and compared, with AI feedback and optional
        native-speaker review.
      </p>

      <ComparisonTable
        leftTitle="Challenge mechanic"
        rightTitle="Learning value"
        leftColor="purple"
        rightColor="green"
        rows={[
          ["same scene, same role", "fair comparison"],
          ["scores include hints and retries", "independence becomes visible"],
          ["AI evaluates speech performance", "pronunciation and timing feedback"],
          ["native speaker can review", "human assessment can override AI"],
        ]}
      />

      <BigStatement
        title="Challenge mode turns repetition into social rehearsal."
        tone="purple"
        compact
      >
        Learners do not only practice alone. They can compare performance,
        receive feedback, and invite others into the same dialogue task.
      </BigStatement>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
},
{
  title: "Shared Rehearsal",
  density: "dense",
  content: (
    <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">

      <div className="mx-auto max-w-4xl rounded-3xl border border-pink-100 bg-white/85 px-5 py-4 shadow-sm">
        <div className="text-xs font-black uppercase tracking-[0.18em] text-pink-700">
          Collaborative mode
        </div>

        <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
          Dialogue practice becomes more engaging when another learner’s voice
          becomes part of the scene.
        </div>
      </div>

      <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
        Learners can rehearse against recordings from other users.
      </h3>

      <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
        One learner records one role, another learner performs the opposite role.
        The dialogue becomes a partially social interaction rather than isolated repetition.
      </p>

      <ComparisonTable
        leftTitle="Interactive feature"
        rightTitle="Social effect"
        leftColor="purple"
        rightColor="green"
        rows={[
          ["another learner provides cue lines", "practice feels more alive"],
          ["different voices and delivery styles", "greater listening adaptability"],
          ["recorded dialogue exchange", "shared participation in the same scene"],
          ["challenge and rehearsal modes", "motivation through collaboration"],
        ]}
      />

      <BigStatement
        title="The long-term goal is meaningful social rehearsal."
        tone="purple"
        compact
      >
        Online rehearsal can later connect naturally to language cafés,
        reading groups, workshops, and offline dialogue practice with a shared purpose.
      </BigStatement>
    </div>
  ),
  bgColor:
    "bg-[radial-gradient(circle_at_top_left,_#fce7f3,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
}
  ],
},
  description: "rehearsal & dialogue challenges",
  slides: [
    {
      title: "Speech As Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Central analogy
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              An actor learns a role by rehearsing lines together with others.
              Speech practice can work in a similar way.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Learners rehearse dialogue roles.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system plays the opposite role while the learner practices responses,
            timing, pronunciation, and delivery inside a structured interaction.
          </p>

          <div className="grid gap-4 md:grid-cols-3 xl:gap-5">
            <SoftCard className="text-left ring-1 ring-blue-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                Round 1
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Learn the lines
              </h4>

              <CleanList
                accent="blue"
                items={[
                  "hear all dialogue lines",
                  "repeat own and opponent lines",
                  "practice pronunciation and rhythm",
                  "become familiar with the scene",
                ]}
              />
            </SoftCard>

            <SoftCard className="text-left ring-1 ring-purple-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-purple-700">
                Round 2
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Rehearse the scene
              </h4>

              <CleanList
                accent="purple"
                items={[
                  "only opponent lines are played",
                  "produce missing responses",
                  "practice timing and recall",
                  "stay inside interaction flow",
                ]}
              />
            </SoftCard>

            <SoftCard className="text-left ring-1 ring-pink-100">
              <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-pink-700">
                Round 3
              </div>

              <h4 className="mb-3 text-xl font-black text-gray-950">
                Perform the role
              </h4>

              <CleanList
                accent="pink"
                items={[
                  "minimal or no support",
                  "score depends on hint usage",
                  "AI evaluates performance",
                  "pronunciation and timing assessment",
                ]}
              />
            </SoftCard>
          </div>

          <BigStatement
            title="The learner does not only memorize sentences."
            tone="purple"
            compact
          >
            The learner rehearses timing, response, pronunciation,
            and participation inside structured interaction.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },

    {
      title: "Support And Progression",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-blue-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-blue-700">
              Guided difficulty
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              Support gradually disappears as the learner becomes more confident.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Hints function as temporary scaffolding.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The system can provide different levels of support while keeping
            the learner inside the interaction loop.
          </p>

          <ComparisonTable
            leftTitle="Hint type"
            rightTitle="Learning function"
            leftColor="blue"
            rightColor="purple"
            rows={[
              ["first-word cue", "small retrieval support"],
              ["shuffled words", "sentence reconstruction"],
              ["audio replay", "prosody and pronunciation support"],
              ["spoken line + text", "maximum scaffold"],
            ]}
          />

          <BigStatement
            title="More support gives lower scores."
            tone="green"
            compact
          >
            The scoring system rewards increasing independence,
            while still allowing learners to continue practicing safely.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_30%),linear-gradient(135deg,#ffffff,#eff6ff)]",
    },

    {
      title: "Why The Game Loop Supports Learning",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-emerald-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-700">
              Pedagogical logic
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              The game mechanics are derived from rehearsal, retrieval,
              scaffolding, and repeated exposure to useful language patterns.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            The interaction loop supports speech automaticity.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            The learner responds to a spoken cue, not just a written prompt.
            This trains pronunciation, intonation, rhythm, and timing inside
            a realistic dialogue exchange.
          </p>

          <ComparisonTable
            leftTitle="Learning principle"
            rightTitle="Interactive implementation"
            leftColor="green"
            rightColor="purple"
            rows={[
              ["repeated language exposure", "hear dialogue patterns many times"],
              ["chunk and pattern learning", "practice complete useful responses"],
              ["retrieval practice", "produce missing lines from memory"],
              ["prosodic response", "answer spoken cues with rhythm and intonation"],
              ["gradual scaffolding removal", "reduce hints over time"],
            ]}
          />

          <BigStatement
            title="The score measures independence, delivery, and timing."
            tone="green"
            compact
          >
            Fewer hints, fewer retries, clearer pronunciation, and more natural intonation
            show movement from guided rehearsal toward increasingly automatic spoken participation.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#dcfce7,_transparent_30%),radial-gradient(circle_at_bottom_right,_#f3e8ff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
    },

    {
      title: "Challenge & Shared Rehearsal",
      density: "dense",
      content: (
        <div className="mx-auto max-w-6xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
          <div className="mx-auto max-w-4xl rounded-3xl border border-purple-100 bg-white/85 px-5 py-4 shadow-sm">
            <div className="text-xs font-black uppercase tracking-[0.18em] text-purple-700">
              Social practice layer
            </div>

            <div className="mt-2 text-base italic leading-relaxed text-gray-700 sm:text-lg">
              When a learner is ready, the same scene can become a challenge,
              a shared recording, or preparation for offline rehearsal.
            </div>
          </div>

          <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
            Rehearsal becomes social when learners perform the same scene.
          </h3>

          <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
            Learners can challenge others on the same role, perform against
            another user’s recorded lines, and receive AI feedback with optional
            native-speaker review.
          </p>

          <ComparisonTable
            leftTitle="Social mechanic"
            rightTitle="Learning value"
            leftColor="purple"
            rightColor="green"
            rows={[
              ["challenge another learner", "same scene creates fair comparison"],
              ["use another user’s cue lines", "practice feels more alive"],
              ["AI scores pronunciation and timing", "feedback remains immediate"],
              ["native speaker can review", "human assessment can override AI"],
            ]}
          />

          <BigStatement
            title="The long-term goal is purposeful social rehearsal."
            tone="purple"
            compact
          >
            Online challenges can connect naturally to language cafés,
            reading groups, workshops, and offline dialogue practice.
          </BigStatement>
        </div>
      ),
      bgColor:
        "bg-[radial-gradient(circle_at_top_left,_#f3e8ff,_transparent_30%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#faf5ff)]",
    },
  ],
},

    business: {
      label: "Business",
      description: "monetization and team",
      slides: [
        {
          title: "Monetization & Partnerships",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <ConceptMark emoji="💼" label="Business" sub="community-first model" />
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Monetization follows the learning model.
              </h3>
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Catchphraser can become infrastructure for organized speech practice,
                guided rehearsal, narrated content, institutional speaking activities,
                and creator-led courses.
              </p>

              <div className="grid gap-4 md:grid-cols-4 xl:gap-5">
                <EmojiCard
                  emoji="🎧"
                  title="Publishers"
                  text="narrated content as interactive practice"
                  className="ring-1 ring-blue-100"
                />
                <EmojiCard
                  emoji="🎓"
                  title="Courses"
                  text="structured courses and revenue sharing"
                  className="ring-1 ring-amber-100"
                />
                <EmojiCard
                  emoji="🧑‍🏫"
                  title="Guided activities"
                  text="paid rooms, workshops, rehearsal groups"
                  className="ring-1 ring-purple-100"
                />
                <EmojiCard
                  emoji="🏛️"
                  title="Institutions"
                  text="university language cafés and cohorts"
                  className="ring-1 ring-emerald-100"
                />
              </div>

              <BigStatement title="The first offer: structured social practice and course infrastructure." tone="green" compact>
                Free peer exchange can bring users in. Guided rooms, institutional cohorts,
                publisher content, creator courses, and specialized workshops create monetization.
              </BigStatement>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#dcfce7,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },

        {
          title: "Long-Term Advantage",
          density: "medium",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <h3 className="text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                The platform becomes harder to copy as the ecosystem grows.
              </h3>
              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-700 sm:text-lg xl:text-xl">
                Learners, institutions, publishers, and guided events create accumulated value:
                speech data, content, relationships, and community habits.
              </p>

              <ComparisonTable
                leftTitle="Accumulated knowledge"
                rightTitle="Content network"
                leftColor="green"
                rightColor="amber"
                rows={[
                  ["learner attempts create signal", "native-speaker audio creates quality"],
                  ["peer comments reveal patterns", "publishers get a new channel"],
                  ["L1-specific errors accumulate", "content becomes interactive practice"],
                  ["recommendations improve", "better material attracts learners"],
                ]}
              />

              <BigStatement title="A competitor can copy a feature, but not the accumulated ecosystem." tone="amber" compact>
                The long-term advantage is L1-specific pronunciation data,
                licensed practice material, institutional relationships,
                and a growing speech community.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#ffffff,#fffbeb)]",
        },

        {
          title: "Content & Course Platform",
          density: "dense",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4">
              <h3 className="mx-auto max-w-5xl text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                Narrated content and courses become interactive speech practice.
              </h3>

              <p className="mx-auto max-w-4xl text-base leading-relaxed text-gray-600 sm:text-lg">
                Audiobooks, dialogues, graded readers, pronunciation courses, and
                teacher-made materials can become rehearsal, shadowing, feedback, and
                collaborative speaking experiences.
              </p>

              <div className="grid gap-5 md:grid-cols-2">
                <SoftCard className="bg-gradient-to-br from-amber-50 to-white text-left ring-1 ring-amber-200">
                  <div className="mb-4 text-center">
                    <div className="mb-1 text-xs font-black uppercase tracking-[0.18em] text-amber-700">
                      Creator value
                    </div>

                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      New distribution layer
                    </h4>
                  </div>

                  <NumberedList
                    accent="amber"
                    items={[
                      "turn narrated material into interactive speech training",
                      "subscription or course revenue",
                      "developer and teacher publishing portal",
                      "platform handles rehearsal and delivery",
                    ]}
                  />
                </SoftCard>

                <SoftCard className="bg-gradient-to-br from-blue-50 to-white text-left ring-1 ring-blue-200">
                  <div className="mb-4 text-center">
                    <div className="mb-1 text-xs font-black uppercase tracking-[0.18em] text-blue-700">
                      Learner value
                    </div>

                    <h4 className="text-xl font-black text-gray-950 xl:text-2xl">
                      Structured speaking progression
                    </h4>
                  </div>

                  <NumberedList
                    accent="blue"
                    items={[
                      "high-quality speech models",
                      "practice with AI and human feedback",
                      "reading + listening + speaking integration",
                      "social rehearsal around shared material",
                    ]}
                  />
                </SoftCard>
              </div>

              <BigStatement title="This is not only content licensing." tone="amber" compact>
                The platform transforms narrated material and expert-made courses into
                reusable speech-practice infrastructure.
              </BigStatement>
            </div>
          ),
          bgColor: "bg-[linear-gradient(135deg,#fffdf5,#fffbeb)]",
        },

        {
          title: "The Catchphraser Team",
          density: "team",
          content: (
            <div className="mx-auto max-w-7xl space-y-5 px-3 py-1 text-center sm:px-4 xl:space-y-6">
              <div className="text-center">
                <h2 className="mb-3 text-3xl font-black leading-tight text-gray-950 md:text-5xl">
                  The Catchphraser Team
                </h2>
                <p className="mx-auto max-w-3xl text-base leading-relaxed text-gray-600 sm:text-lg xl:text-xl">
                  Researchers and language professionals building pronunciation training and rehearsal infrastructure.
                </p>
              </div>

              <div className="mx-auto grid max-w-6xl gap-5 md:grid-cols-3 xl:gap-6">
                {[
                  [
                    "it.jpg",
                    "Dr. Inna Tolskaya",
                    "CEO – Product & Linguistics",
                    "itolsk@gmail.com",
                    "Linguist, project manager, and software developer with a passion for innovation and e-learning.",
                    "from-blue-50 to-indigo-50 border-blue-200 text-blue-600",
                    "IT",
                  ],
                  [
                    "oh.jpg",
                    "Assoc. Prof. Olaf Husby",
                    "CCO – Teaching & Didactics",
                    "olafhusby@gmail.com",
                    "Phonetician, second-language educator, course developer, and Norwegian dialects evangelist.",
                    "from-purple-50 to-violet-50 border-purple-200 text-purple-600",
                    "OH",
                  ],
                  [
                    "ds.jpg",
                    "Dr. Dmitry Shcherbin",
                    "CTO/CPO – Tech & Research",
                    "dmitry.shcherbin@gmail.com",
                    "Product leader, software developer, data analyst, and researcher with an interest in practical applications.",
                    "from-cyan-50 to-teal-50 border-cyan-200 text-cyan-600",
                    "DS",
                  ],
                ].map(([img, name, role, email, bio, cardColor, initials]) => (
                  <div
                    key={name}
                    className={`rounded-3xl border bg-gradient-to-br ${cardColor} p-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] xl:p-5`}
                  >
                    <div className="text-center">
                      <div className="relative mx-auto mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-4 border-white bg-gray-100 shadow-xl xl:h-24 xl:w-24">
                        <div className="absolute inset-0 flex items-center justify-center text-3xl font-black text-gray-500">
                          {initials}
                        </div>
                        <img
                          src={asset(img)}
                          alt={name}
                          className="relative z-10 h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.style.display = "none";
                          }}
                        />
                      </div>

                      <h3 className="mb-2 text-lg font-black text-gray-950 xl:text-xl">
                        {name}
                      </h3>
                      <p className="mb-2 text-sm font-bold">{role}</p>
                      <a href={`mailto:${email}`} className="break-all text-xs hover:underline">
                        {email}
                      </a>
                    </div>

                    <div className="mt-4 rounded-2xl border border-white/60 bg-white/85 p-4 shadow-sm backdrop-blur-sm">
                      <p className="text-sm leading-relaxed text-gray-700">{bio}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="text-center">
                <p className="mb-1 text-sm text-gray-700 sm:text-base">
                  Interested in collaboration, testing, or research partnerships?
                </p>
                <a href="mailto:info@catchphraser.com" className="text-base font-bold text-blue-600 hover:underline">
                  info@catchphraser.com
                </a>
              </div>
            </div>
          ),
          bgColor:
            "bg-[radial-gradient(circle_at_top_left,_#dbeafe,_transparent_32%),radial-gradient(circle_at_bottom_right,_#ecfeff,_transparent_30%),linear-gradient(135deg,#ffffff,#f8fafc)]",
        },
      ],
    },
  };
};